import * as React from 'react';
import { makeStyles } from '@material-ui/core';
import clsx from 'clsx';
import { fontFamilies } from '../../theme/text';
import useStackedBarChart from './useStackedBarChart';
import { select } from 'd3';
import BarChartTooltip from './stacked-bar-tooltip';

/**
 * A single event representing a bar in the chart including a date (x axis) and
 * any number of data points to be rendered as stacked bars.
 */
export interface StackedBarEvent {
  date: Date;
  /**
   * Any other keys should be treated as a number but typescript includes date
   * as a string index so we must include Date.
   * See https://github.com/microsoft/TypeScript/issues/17867
   */
  [dataKey: string]: number | Date;
}

export interface StackedBarChartProps {
  /** Array of data receipt events */
  data: StackedBarEvent[];
  /** className for div container css overrides */
  containerClassName?: string;
  /** Key matching keys in the events in the date to names and colors used throughout the viz */
  legend: BarChartLegendItem[];
  /** Display options for the chart */
  options?: StackedBarChartOptions;
}

/**
 * Each item in the legend corresponding to keys in the data (StackedBarEvents)
 */
export interface BarChartLegendItem {
  /** The color used in the viz to represent the data key */
  color: string;
  /** The display name used in tooltips and legend, if displayed */
  name: string;
  /** The key identifying the corresponding item in the StackedBarEvent dataKey */
  key: string;
}

/**
 * Display options
 */
export interface StackedBarChartOptions {
  showXAxis?: boolean;
  showLegend?: boolean;
  showDots?: boolean;
  showYValues?: boolean;
  barWidth?: number;
  svgWidth?: number;
  svgHeight?: number;
  /** Whether the max date of the x axis is now or the latest date in the dataset */
  relativeToNow?: boolean;
}

const defaultOptions: StackedBarChartOptions = {
  showXAxis: true,
  showLegend: true,
  showDots: true,
  showYValues: true,
  svgWidth: 800,
  svgHeight: 200,
  relativeToNow: false
};

const useStyles = makeStyles({
  root: {
    width: '100%',
    height: '100%'
  },
  svg: {
    fontFamily: fontFamilies.body,
    height: '100%',
    width: '100%'
  },
  hoverMask: {
    visibility: 'hidden',
    pointerEvents: 'all',
    cursor: 'crosshair'
  },
  hoverBar: {
    pointerEvents: 'all',
    cursor: 'crosshair',
    '& .hover:hover': {
      fill: 'rgba(0,0,0,.2)'
    }
  },
  legend: {
    display: 'flex',
    justifyContent: 'flex-end',
    '& div': {
      margin: '0 3px',
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center'
    }
  },
  labelColor: {
    height: '.7em',
    width: '.7em'
  },
  label: {
    fontFamily: fontFamilies.body,
    fontSize: '.8em',
    margin: 0
  }
});

/**
 * ## Stacked Bar Chart
 *
 * Render a stacked bar chart on a time axis using data of type StackedBarEvent
 */
const StackedBarChart = ({ data, containerClassName, legend, options }: StackedBarChartProps) => {
  const classes = useStyles();
  const localOptions: StackedBarChartOptions = React.useMemo(() => {
    if (options) {
      const tempOptions = { ...defaultOptions };
      for (const option in options) {
        tempOptions[option] = options[option];
      }
      return tempOptions;
    } else {
      return defaultOptions;
    }
  }, [options]);
  const { margin, x, y, xAxis, yAxis, series } = useStackedBarChart(data, localOptions);
  const xAxisRef = React.useRef<SVGGElement>();
  const yAxisRef = React.useRef<SVGGElement>();
  const barsRef = React.useRef<SVGGElement>();
  const outOfBoundsRef = React.useRef<SVGGElement>();
  const hoverRef = React.useRef<SVGGElement>();
  const dotsRef = React.useRef<SVGGElement>();
  const [tooltip, setTooltip] = React.useState<{
    x: number;
    y: number;
    content: {
      date: Date;
      data: { name: string; amount: number }[];
    };
  } | null>();

  React.useEffect(() => {
    if (legend.length != series.length) {
      throw Error('Bar Chart: Number of items in legend and data do not match');
    }
  }, [series, legend]);

  /**
   * Render X Axis
   */
  React.useEffect(() => {
    if (localOptions.showXAxis) {
      select(xAxisRef.current).call(xAxis);
    } else if (!localOptions.showXAxis && xAxisRef.current && xAxisRef.current.firstChild) {
      // if showXAxis changes to false, remove axis
      while (xAxisRef.current.firstChild) {
        xAxisRef.current.removeChild(xAxisRef.current.firstChild);
      }
    }
  }, [xAxis, localOptions]);

  /**
   * Render Y Axis
   */
  React.useEffect(() => {
    select(yAxisRef.current).call(yAxis);
  }, [yAxis]);

  /**
   * Render Bars and Dots
   */
  React.useEffect(() => {
    let barWidth: number;
    if (localOptions.barWidth) {
      barWidth = localOptions.barWidth;
    } else if (data.length < 10) {
      barWidth = 12;
    } else if (data.length < 40) {
      barWidth = 8;
    } else if (data.length < 80) {
      barWidth = 6;
    } else {
      barWidth = 4;
    }

    select(barsRef.current)
      .selectAll('g')
      .data(series)
      .join('g')
      .attr('fill', (d) => legend.find((l) => l.key == d.key).color)
      .selectAll('rect')
      .data((d) => d)
      .join('rect')
      .attr('x', (d, i) => x(d.data.date) - barWidth / 2)
      .attr('y', (d) => y(d[1]))
      .attr('height', (d) => y(d[0]) - y(d[1]))
      .attr('width', barWidth);

    select(outOfBoundsRef.current)
      .selectAll('g')
      .data(series)
      .join('g')
      .lower() // reverse the order so that the color of lower bars takes precendence
      .attr('fill', (d) => legend.find((l) => l.key == d.key).color)
      .selectAll('polygon')
      .data((events) => events.filter((event) => y(event[1]) == margin.top))
      .join('polygon')
      .attr('points', (d) => {
        const xcenter = x(d.data.date);
        return `${xcenter - barWidth}, ${margin.top} ${xcenter}, ${margin.top - barWidth - 2} ${
          xcenter + barWidth
        }, ${margin.top}`;
      });

    select(hoverRef.current)
      .selectAll('rect')
      .data(data)
      .join('rect')
      .attr('x', (d, i) => x(d.date) - barWidth / 2)
      .attr('y', margin.top)
      .attr('height', localOptions.svgHeight - margin.top - margin.bottom)
      .attr('width', barWidth)
      .attr('fill', 'transparent')
      .attr('class', 'hover')
      .on('mouseenter', (e, d) => {
        setTooltip({
          x: x(d.date) - 4,
          y: margin.top / 2,
          content: {
            date: d.date,
            data: legend.map((l) => {
              return {
                name: l.name,
                amount: d[l.key] as number
              };
            })
          }
        });
      })
      .on('mouseleave', () => {
        setTooltip(null);
      });

    if (localOptions.showDots) {
      let dotWidth: number;
      if (data.length < 40) {
        dotWidth = 8;
      } else if (data.length < 80) {
        dotWidth = 6;
      } else {
        dotWidth = 4;
      }
      select(dotsRef.current)
        .selectAll('circle')
        .data(series[0])
        .join('circle')
        .attr('cx', (d) => x(d.data.date))
        .attr('cy', localOptions.svgHeight - margin.bottom + 8)
        .attr('r', dotWidth / 2)
        .attr('fill', '#1F82BF')
        .attr('class', 'dot');
    } else if (!localOptions.showDots && dotsRef.current && dotsRef.current.firstChild) {
      // if dots change to false, remove dots
      while (dotsRef.current.firstChild) {
        dotsRef.current.removeChild(dotsRef.current.firstChild);
      }
    }
  }, [x, y, series, localOptions]);

  return (
    <div className={clsx(classes.root, containerClassName)}>
      <svg
        className={classes.svg}
        viewBox={`0 0 ${localOptions.svgWidth} ${localOptions.svgHeight}`}
      >
        <g ref={yAxisRef} />
        {localOptions.showXAxis && <g ref={xAxisRef} />}
        <g ref={barsRef} />
        <g ref={outOfBoundsRef} />
        <rect
          x={margin.left}
          y={margin.top}
          width={localOptions.svgWidth - margin.top - margin.left}
          height={localOptions.svgHeight - margin.top - margin.bottom}
          className={classes.hoverMask}
        />
        <g ref={hoverRef} className={classes.hoverBar}></g>
        {localOptions.showDots && <g ref={dotsRef}></g>}
        {tooltip && (
          <BarChartTooltip {...tooltip} svgWidth={localOptions.svgWidth} legend={legend} />
        )}
      </svg>
      {localOptions.showLegend && (
        <div className={classes.legend}>
          {legend.map((label) => (
            <div key={`label-${label.name}`}>
              <div className={classes.labelColor} style={{ backgroundColor: `${label.color}` }} />
              <p className={classes.label}>{label.name}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default StackedBarChart;
